import React from 'react'
import styled, { css } from 'styled-components'

import { StatelessDropdown } from 'components/Dropdown'
import { dropdownItemStyles } from 'components/Dropdown/Item/DropdownItem.styles'
import ExportableIcon from 'modules/shares/Inspector/ExportableIcon'

import { truncateStyles } from 'components/Truncate'

import { LayerTree } from './layerTree'
import { SelectionType } from 'modules/shares/types'

import { ReactComponent as ArtboardIcon } from 'icons/context-menu-artboard.svg'
import { ReactComponent as TextIcon } from 'icons/context-menu-text-layer.svg'
import { ReactComponent as ShapeIcon } from 'icons/context-menu-shape-layer.svg'
import { ReactComponent as GroupIcon } from 'icons/context-menu-group.svg'
import { ReactComponent as BitmapIcon } from 'icons/context-menu-bitmap-layer.svg'
import { ReactComponent as SliceIcon } from 'icons/inspector-slice.svg'
import { ReactComponent as SymbolIcon } from 'icons/inspector-symbol-17.svg'

interface ContextMenuLayerIconProps {
  selection: SelectionType
}

// Gets the layer icon url based on the layer type.
const ContextMenuLayerIcon = ({ selection }: ContextMenuLayerIconProps) => {
  switch (selection) {
    case 'artboard':
      return <ArtboardIcon width={17} height={17} />
    case 'bitmap':
      return <BitmapIcon width={17} height={17} />
    case 'text':
      return <TextIcon width={17} height={17} />
    case 'group':
      return <GroupIcon width={17} height={17} />
    case 'slice':
      return <SliceIcon width={17} height={17} />
    case 'symbol':
      return <SymbolIcon width={17} height={17} />
    default:
      return <ShapeIcon width={17} height={17} />
  }
}

export const DropdownWrapper = styled(StatelessDropdown)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  position: absolute;
`

export const ContextMenuNav = styled.nav`
  /* max height = 100% viewport height - header height - extra margin */
  max-height: ${({ theme }) => `calc(100vh - ${theme.header.height} - 70px);`};
  overflow: auto;
  background: white;
  margin: 0 8px;

  ol {
    margin-top: 0;
    margin-bottom: 0;
    list-style-type: none;
    padding: 0;
  }

  p {
    margin-top: 0;
    margin-bottom: 0;
    font-size: 0.875rem;
    width: 100%;
    ${truncateStyles};
  }
`

interface ContextMenuLabelProps {
  level: number
  isSelected: boolean
}

export const ContextMenuLabel = styled.div.attrs(
  (props: ContextMenuLabelProps) => ({
    'aria-selected': props.isSelected,
  })
)<ContextMenuLabelProps>(
  ({ theme, level, isSelected }) => css`
    ${dropdownItemStyles};

    display: flex;
    align-items: center;

    height: 32px;
    width: 100%;

    padding-left: ${theme.space[3] + level * theme.space[3]}px;

    /* && is needed to increase specificity and avoid showing the hover state
    for a selected item */
    && {
      background-color: ${isSelected
        ? theme.colors.primaryTint[10]
        : undefined};
      color: ${isSelected ? theme.colors.primaryTint[100] : undefined};

      [data-exportable-icon] {
        /* This background can't be with alpha because the purpose is to
        generate a border around the knife icon and hide the icon that is
        behind, so in this case #f8eee5 is emulating the background of a
        selected element (theme.colors.background.tertiary[1]) */
        background: ${isSelected ? '#f8eee5' : undefined};
      }
    }
  `
)

interface IconProps {
  maskColor?: string
  layerTree: LayerTree
  className?: string
}

const BaseIcon = ({ layerTree: tree, maskColor, className }: IconProps) => (
  <div className={className}>
    <ContextMenuLayerIcon selection={tree.type} />
    {/* Slice already has an exportable icon present on the icon */}
    {tree.exportable && tree.type !== 'slice' && (
      <ExportableIcon maskColor={maskColor} />
    )}
  </div>
)

export const Icon = styled(BaseIcon)`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 8px;
`
